import React, { useState } from 'react';
import { X, Copy, Check } from 'lucide-react';

const DebugLogModal = ({ log, onClose }) => {
    const [activeTab, setActiveTab] = useState('prompt');
    const [copied, setCopied] = useState(false);

    if (!log) return null;

    const handleCopy = () => {
        const content = activeTab === 'prompt' ? log.user_prompt : log.llm_response;
        navigator.clipboard.writeText(content);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    };

    const formatJSON = (str) => {
        try {
            return JSON.stringify(JSON.parse(str), null, 2);
        } catch (e) {
            return str;
        }
    };

    const displayContent = activeTab === 'prompt'
        ? log.user_prompt
        : formatJSON(log.llm_response);

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
            <div className="bg-white rounded-xl shadow-2xl w-full max-w-5xl h-[85vh] flex flex-col overflow-hidden">
                {/* Header */}
                <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 bg-gray-50">
                    <div>
                        <h2 className="text-lg font-semibold text-gray-900">Log Details #{log.id}</h2>
                        <div className="flex items-center gap-4 mt-1 text-xs text-gray-500 font-mono">
                            <span>Model: {log.llm_model}</span>
                            <span>•</span>
                            <span>Tokens: {log.tokens_used || 'N/A'}</span>
                            <span>•</span>
                            <span>Time: {log.processing_time_ms ? `${log.processing_time_ms}ms` : 'N/A'}</span>
                        </div>
                        {log.parameters && (
                            <div className="flex items-center gap-2 mt-1 text-xs text-gray-500 font-mono">
                                <span>Params: {JSON.stringify(log.parameters)}</span>
                            </div>
                        )}
                    </div>
                    <button
                        onClick={onClose}
                        className="p-2 hover:bg-gray-200 rounded-full transition-colors text-gray-500 hover:text-gray-700"
                    >
                        <X size={20} />
                    </button>
                </div>

                {/* Tabs */}
                <div className="flex border-b border-gray-200">
                    <button
                        onClick={() => setActiveTab('prompt')}
                        className={`flex-1 px-6 py-3 text-sm font-medium transition-colors border-b-2 ${activeTab === 'prompt'
                                ? 'border-blue-500 text-blue-600 bg-blue-50/50'
                                : 'border-transparent text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                            }`}
                    >
                        Prompt (Input)
                    </button>
                    <button
                        onClick={() => setActiveTab('response')}
                        className={`flex-1 px-6 py-3 text-sm font-medium transition-colors border-b-2 ${activeTab === 'response'
                                ? 'border-blue-500 text-blue-600 bg-blue-50/50'
                                : 'border-transparent text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                            }`}
                    >
                        Response (Output)
                    </button>
                </div>

                {/* Content */}
                <div className="flex-1 overflow-auto bg-gray-50 relative group">
                    <button
                        onClick={handleCopy}
                        className="absolute top-4 right-4 p-2 bg-white/90 backdrop-blur border border-gray-200 rounded-lg shadow-sm
                                 text-gray-500 hover:text-gray-900 transition-all opacity-0 group-hover:opacity-100 z-10"
                        title="Copy content"
                    >
                        {copied ? <Check size={16} className="text-green-600" /> : <Copy size={16} />}
                    </button>
                    <div className="p-6">
                        <pre className="font-mono text-sm bg-white p-6 rounded-lg border border-gray-200 whitespace-pre-wrap break-words text-gray-800 shadow-sm">
                            {displayContent}
                        </pre>
                    </div>
                </div>

                {/* Footer */}
                <div className="px-6 py-4 border-t border-gray-200 bg-gray-50 flex justify-end">
                    <button
                        onClick={onClose}
                        className="px-4 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 text-gray-700 text-sm font-medium transition-colors"
                    >
                        Close
                    </button>
                </div>
            </div>
        </div>
    );
};

export default DebugLogModal;
